import { Grid, IconButton, Typography } from '@material-ui/core';
import { FC, ReactElement, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { IDictionary } from '~/services/api/dictionaries/types';
import { useStoreState } from '~/store/hooks';
import { ReactComponent as DeleteIcon } from '~/ui/assets/images/delete.svg';
import smallAvatarIcon from '~/ui/assets/images/smallAvatar.svg';
import variables from '~/ui/assets/styles/colors.module.scss';
import PrivateImage from '~/ui/components/common/PrivateImage';

import { getClientMemberFormFieldName } from '../../../../helpers/getters';
import MemoField from './MemoField';
import TeamsField from './TeamsField';

import styles from '../../AccordionMembers.module.scss';

interface IProps {
  clientName: string;
  clientPhoto: string | null;
  clientId: number | string;
  fieldNameMemberId: string;
  handleRemoveClient: (id: number, field: string) => void;
  teams?: (IDictionary & { isPrimary: boolean })[];
  memberId: number;
  validateAvailability?: boolean;
  isGlobalUser: boolean;
}

const AllocationMemberClientForm: FC<IProps> = ({
  clientName,
  clientPhoto,
  clientId,
  fieldNameMemberId,
  isGlobalUser,
  teams,
  handleRemoveClient,
  memberId,
  validateAvailability = false,
}): ReactElement => {
  const {
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const { dateAvailability } = useStoreState(state => state.clientAllocation);

  const alertMessage = useMemo(() => {
    if (!dateAvailability?.isValid && validateAvailability) {
      const currentMemberAndClientAvailability = dateAvailability?.clients.find(
        c => c.clientId === clientId && !c.isDifferentUser && c.userId === memberId,
      );

      if (currentMemberAndClientAvailability) {
        return `Alert: client is already allocated to this user for this date${
          currentMemberAndClientAvailability.isDifferentTeam ? ' on another team' : ''
        }. Please remove in order to save.`;
      }
    }

    return null;
  }, [dateAvailability, clientId, validateAvailability, memberId]);

  const noteMessage = useMemo(() => {
    const currentMemberAndClientAvailability = dateAvailability?.clients.find(
      c => c.clientId === clientId && c.isDifferentUser && c.userId === memberId,
    );

    if (validateAvailability && !dateAvailability?.isValid && currentMemberAndClientAvailability) {
      return `Note: this client is allocated on this date${
        currentMemberAndClientAvailability.isDifferentTeam ? ' for another team.' : '.'
      }`;
    }

    return null;
  }, [dateAvailability, clientId, validateAvailability, memberId]);

  useEffect(() => {
    if (alertMessage && !errors[`clients${fieldNameMemberId}`]) {
      setError(`clients${fieldNameMemberId}`, {
        message: '',
      });
    } else if (!alertMessage && errors[`clients${fieldNameMemberId}`]) {
      clearErrors(`clients${fieldNameMemberId}`);
    }
  }, [errors, fieldNameMemberId, setError, clearErrors, alertMessage]);

  const memoFieldName = useMemo(
    () => getClientMemberFormFieldName('memo_', clientId as number, fieldNameMemberId),
    [clientId, fieldNameMemberId],
  );

  const teamIdFieldName = useMemo(
    () => getClientMemberFormFieldName('teamId_', clientId as number, fieldNameMemberId),
    [clientId, fieldNameMemberId],
  );

  return (
    <Grid container key={clientId} alignItems="center" className={styles.clientDetailItem}>
      <Grid item xs={4} className={styles.selectedClient}>
        <PrivateImage
          className={styles.image}
          src={clientPhoto || smallAvatarIcon}
          height={40}
          alt="avatar"
        />
        <Typography>{clientName}</Typography>
      </Grid>
      <Grid item xs={2}>
        {teams && isGlobalUser && <TeamsField teams={teams} fieldName={teamIdFieldName} />}
      </Grid>
      <Grid item xs={6} className={styles.memoContainer}>
        <MemoField memoFieldName={memoFieldName} />
        <IconButton
          onClick={() => handleRemoveClient(clientId as number, `clients${fieldNameMemberId}`)}
        >
          <DeleteIcon width={20} color={variables.colorRed} />
        </IconButton>
      </Grid>
      {alertMessage && (
        <Grid item sm={12} className={styles.validationErrors}>
          {alertMessage}
        </Grid>
      )}
      {noteMessage && (
        <Grid item sm={12} className={styles.validationErrors}>
          {noteMessage}
        </Grid>
      )}
    </Grid>
  );
};

export default AllocationMemberClientForm;
